import {
  FccCabinSafetyCheckStatus,
  FccWaypointDeliveryActionStatus,
  FccWaypointPickupActionStatus,
} from '@backoffice-frontend/graphql'
import { usePrevious } from '@backoffice-frontend/hooks'
import type { CircularProgressProps } from '@backoffice-frontend/patterns'
import {
  CircularProgress,
  MoiaGrid,
  useTheme,
} from '@backoffice-frontend/patterns'
import {
  CheckmarkIcon,
  IdCardIcon,
  PersonMinusIcon,
  PersonPlusIcon,
  ShieldCheckIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import { Text } from '@moia-dev/pace-web'
import { motion, useAnimationControls } from 'framer-motion'
import size from 'lodash/size'
import type { CSSProperties } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
  DEFAULT_TRANSITION,
  TRANSITION_DURATION_IN_SEC,
} from '../../constants'
import type { WaypointActionsStatus } from '../actionStatus'

const COLOR_FADE_DURATION = 0.1
const MOVE_BACKGROUND_DELAY = 1

const SmallCircularProgress = (props: CircularProgressProps) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{
      duration: COLOR_FADE_DURATION,
      delay: MOVE_BACKGROUND_DELAY + TRANSITION_DURATION_IN_SEC,
    }}
  >
    <CircularProgress
      {...props}
      style={{
        display: 'block',
        width: 18,
        height: 18,
      }}
    />
  </motion.div>
)

export const AdVehicleMissionWaypointActionStates = ({
  waypointActionsStatus,
}: {
  waypointActionsStatus: WaypointActionsStatus
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const backgroundControls = useAnimationControls()

  const SUCCESS = theme.palette.success.superlight
  const NEUTRAL = theme.semantic.ColorSurfaceReduced
  const ERROR = theme.palette.error.superlight

  const firstColumn = {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    zIndex: 1,
  } as const

  const secondColumn = {
    gridColumnStart: 2,
    display: 'flex',
    gap: theme.space.Space2,
    alignItems: 'center',
    color: theme.semantic.ColorContentDefault,
    zIndex: 1,
  } as const

  const previousActionsStatus = usePrevious(waypointActionsStatus)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only state changes trigger animations
  useEffect(() => {
    const deliveryStateChanged =
      previousActionsStatus?.deliveryStatus !==
      waypointActionsStatus.deliveryStatus
    const deliveryCompleted =
      waypointActionsStatus.deliveryStatus ===
      FccWaypointDeliveryActionStatus.Completed

    const pickupStateChanged =
      previousActionsStatus?.pickupStatus !== waypointActionsStatus.pickupStatus
    const pickupCompleted =
      waypointActionsStatus.pickupStatus ===
        FccWaypointPickupActionStatus.Completed ||
      waypointActionsStatus.pickupStatus ===
        FccWaypointPickupActionStatus.NoShow

    const cabinSafetyCheckStateChanged =
      previousActionsStatus?.cabinSafetyCheckStatus !==
      waypointActionsStatus.cabinSafetyCheckStatus
    const cabinSafetyCheckCompleted =
      waypointActionsStatus.cabinSafetyCheckStatus ===
      FccCabinSafetyCheckStatus.Completed

    const changeBackground = (color: CSSProperties['color']) =>
      backgroundControls.start({
        color,
        transition: { duration: COLOR_FADE_DURATION },
      })

    const moveBackground = (top: number) =>
      backgroundControls.start({
        color: NEUTRAL,
        top,
        transition: {
          delay: MOVE_BACKGROUND_DELAY,
          color: { duration: COLOR_FADE_DURATION },
          top: DEFAULT_TRANSITION,
        },
      })

    if (deliveryStateChanged && deliveryCompleted) {
      changeBackground(SUCCESS).then(() =>
        moveBackground(AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT),
      )
    }

    if (pickupStateChanged && pickupCompleted) {
      const color =
        waypointActionsStatus.pickupStatus ===
        FccWaypointPickupActionStatus.NoShow
          ? ERROR
          : SUCCESS
      const top = waypointActionsStatus.deliveryStatus
        ? AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT * 2
        : AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT
      changeBackground(color).then(() => moveBackground(top))
    }

    if (cabinSafetyCheckStateChanged && cabinSafetyCheckCompleted) {
      changeBackground(SUCCESS)
    }
    // Only state changes trigger animations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypointActionsStatus, previousActionsStatus])

  if (waypointActionsStatus.cabinSafetyCheckStatus === undefined) return null

  return (
    <motion.div
      css={theme => ({
        position: 'relative',
        overflow: 'hidden',
        margin: `${theme.space.Space2} ${theme.space.Space4} -${theme.space.Space2}`,
      })}
      initial={{ height: 0 }}
      animate={{
        height:
          size(waypointActionsStatus) *
          AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
      }}
      transition={DEFAULT_TRANSITION}
    >
      <div css={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        {/*Delivery*/}
        {waypointActionsStatus.deliveryStatus && (
          <MoiaGrid
            gridTemplateColumns={'75px 1fr'}
            css={{
              height: AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
            }}
          >
            {waypointActionsStatus.deliveryStatus ===
              FccWaypointDeliveryActionStatus.InProgress && (
              <>
                <div css={firstColumn}>
                  <SmallCircularProgress color="primary" />
                </div>
                <div css={secondColumn}>
                  <PersonMinusIcon />
                  {t('adFleetMap_missionCard_deboarding_label')}
                </div>
              </>
            )}
            {waypointActionsStatus.deliveryStatus ===
              FccWaypointDeliveryActionStatus.Completed && (
              <>
                <div css={firstColumn}>
                  <CheckmarkIcon
                    css={{
                      color: theme.semantic.ColorSuccessDefault,
                    }}
                  />
                </div>
                <div css={secondColumn}>
                  <PersonMinusIcon />
                  {t('adFleetMap_missionCard_deboarding_label')}
                </div>
              </>
            )}
          </MoiaGrid>
        )}

        {/*Pickup*/}
        {waypointActionsStatus.pickupStatus && (
          <MoiaGrid
            gridTemplateColumns={'75px 1fr'}
            css={{
              height: AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
              color: theme.semantic.ColorContentDefault,
            }}
          >
            {waypointActionsStatus.pickupStatus ===
              FccWaypointPickupActionStatus.Pending && (
              <div
                css={{
                  ...secondColumn,
                  color: theme.semantic.ColorContentDisabled,
                }}
              >
                {/* Time plus ID-Icon */}
                <IdCardIcon />
                {t('adFleetMap_missionCard_checkIn_label')}
              </div>
            )}

            {waypointActionsStatus.pickupStatus ===
              FccWaypointPickupActionStatus.InProgress && (
              <>
                <div css={firstColumn}>
                  <SmallCircularProgress color="primary" />
                </div>
                <div css={secondColumn}>
                  <IdCardIcon />
                  {t('adFleetMap_missionCard_checkIn_label')}
                </div>
              </>
            )}

            {waypointActionsStatus.pickupStatus ===
              FccWaypointPickupActionStatus.Completed && (
              <>
                <div css={firstColumn}>
                  <CheckmarkIcon
                    css={{
                      color: theme.semantic.ColorSuccessDefault,
                    }}
                  />
                </div>
                <div css={secondColumn}>
                  <PersonPlusIcon />
                  {t('adFleetMap_missionCard_checkIn_label')}
                </div>
              </>
            )}

            {waypointActionsStatus.pickupStatus ===
              FccWaypointPickupActionStatus.NoShow && (
              <>
                <div css={firstColumn}>
                  {/* TODO: check props - icon refactoring 07/07/25 */}
                  {/* Removed props: color={'error'} */}
                  <XIcon />
                </div>
                <div css={secondColumn}>
                  <IdCardIcon />
                  {t('adFleetMap_missionCard_passengerNoShow_label')}
                </div>
              </>
            )}
          </MoiaGrid>
        )}

        {/* Separator */}
        <MoiaGrid
          gridGap={0}
          gridTemplateColumns={`${theme.space.Space4} 40px 4fr 0 0`}
        >
          <div
            css={theme => ({
              borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              height: 0,
              gridColumnStart: 3,
              gridColumnEnd: -2,
            })}
          />
        </MoiaGrid>

        {/* Cabin Safety Check */}
        <MoiaGrid
          gridTemplateColumns={'75px 1fr'}
          css={{
            height: AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
          }}
        >
          <div css={firstColumn}>
            {waypointActionsStatus.cabinSafetyCheckStatus ===
              FccCabinSafetyCheckStatus.InProgress && (
              <SmallCircularProgress color="primary" />
            )}
            {waypointActionsStatus.cabinSafetyCheckStatus ===
              FccCabinSafetyCheckStatus.Completed && (
              <CheckmarkIcon
                css={{
                  color: theme.semantic.ColorSuccessDefault,
                }}
              />
            )}
          </div>
          <Text
            size="md"
            css={{
              ...secondColumn,
              color:
                waypointActionsStatus.cabinSafetyCheckStatus ===
                FccCabinSafetyCheckStatus.Pending
                  ? theme.semantic.ColorContentDisabled
                  : 'currentcolor',
            }}
          >
            <ShieldCheckIcon />
            {t('adFleetMap_missionCard_cabinSafetyCheck_label')}
          </Text>
        </MoiaGrid>
        <motion.div
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
            color: NEUTRAL,
          }}
          animate={backgroundControls}
        >
          <div
            css={theme => ({
              position: 'absolute',
              top: 5,
              left: 0,
              right: 0,
              bottom: 5,
              backgroundColor: theme.semantic.ColorSurfaceReduced,
              borderRadius: 8,
            })}
          />
        </motion.div>
      </div>
    </motion.div>
  )
}
